import { useEffect, useState } from 'react';
import { TriangleAlert } from 'lucide-react';
import {
  getApiError,
  useCreateNoticeMutation,
  useUpdateNoticeMutation,
  type Group,
  type Notice,
  type NoticePriority,
} from '@/store/api';
import { Button } from '@/components/ui/Button';
import { Field, Input } from '@/components/ui/Field';
import { Modal } from '@/components/ui/Modal';
import { isoToLocalInput, localInputToIso } from '@/lib/datetime';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

/*
  Writing a notice.

  ── Why this dialog cannot publish ─────────────────────────────────────────

  Saving writes a draft or sets a schedule. Sending to every parent in the
  school is a separate button on the list, behind a preview that says how many
  people it reaches. One dialog that composes and broadcasts would make a
  typo in the audience a thing you discover afterwards.

  ── Why SMS is a checkbox with a warning rather than a default ─────────────

  In-app and push cost nothing. SMS bills per recipient, and the audience is
  measured in hundreds. The checkbox says so, and the preview says how many.
*/

export interface NoticeDraft {
  _id?: string;
  title: string;
  body: string;
  priority: NoticePriority;
  groupIds: string[];
  sendPush: boolean;
  sendSms: boolean;
  scheduledFor: string;
}

export function emptyNoticeDraft(): NoticeDraft {
  return {
    title: '',
    body: '',
    priority: 'normal',
    groupIds: [],
    sendPush: true,
    sendSms: false,
    scheduledFor: '',
  };
}

export function toNoticeDraft(notice: Notice): NoticeDraft {
  return {
    _id: notice._id,
    title: notice.title,
    body: notice.body,
    priority: notice.priority,
    groupIds: notice.groupIds,
    sendPush: notice.sendPush,
    sendSms: notice.sendSms,
    scheduledFor: isoToLocalInput(notice.scheduledFor),
  };
}

export function NoticeDialog({
  draft,
  groups,
  institutionId,
  onClose,
}: {
  draft: NoticeDraft | null;
  groups: Group[];
  institutionId: string;
  onClose: () => void;
}) {
  const [form, setForm] = useState<NoticeDraft>(draft ?? emptyNoticeDraft());
  const [error, setError] = useState('');

  const [createNotice, createState] = useCreateNoticeMutation();
  const [updateNotice, updateState] = useUpdateNoticeMutation();
  const saving = createState.isLoading || updateState.isLoading;
  const isEditing = Boolean(form._id);

  useEffect(() => {
    if (draft) setForm(draft);
    setError('');
  }, [draft]);

  const toggleGroup = (id: string) =>
    setForm((f) => ({
      ...f,
      groupIds: f.groupIds.includes(id) ? f.groupIds.filter((g) => g !== id) : [...f.groupIds, id],
    }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!form.title.trim()) return setError('Give the notice a title — it is what parents see first.');
    if (!form.body.trim()) return setError('Write the notice.');

    const scheduledFor = form.scheduledFor ? localInputToIso(form.scheduledFor) : null;
    if (scheduledFor && new Date(scheduledFor) <= new Date()) {
      return setError('A scheduled time has to be in the future. Leave it blank to publish by hand.');
    }

    const data = {
      title: form.title.trim(),
      body: form.body.trim(),
      priority: form.priority,
      groupIds: form.groupIds,
      sendPush: form.sendPush,
      sendSms: form.sendSms,
      scheduledFor,
    };

    try {
      if (form._id) {
        await updateNotice({ institutionId, noticeId: form._id, data }).unwrap();
      } else {
        await createNotice({ institutionId, data: { ...data, scheduledFor: scheduledFor ?? undefined } }).unwrap();
      }
      onClose();
    } catch (err) {
      setError(getApiError(err));
    }
  };

  return (
    <Modal
      open={draft !== null}
      onClose={onClose}
      size="lg"
      title={isEditing ? 'Edit notice' : 'New notice'}
      description="Nobody sees it until you publish it, or until its scheduled time arrives."
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button form="notice-form" type="submit" loading={saving}>
            {form.scheduledFor ? 'Save & schedule' : isEditing ? 'Save changes' : 'Save draft'}
          </Button>
        </div>
      }
    >
      <form id="notice-form" onSubmit={handleSubmit} className="flex flex-col gap-3">
        {error && (
          <div className={cn(styles.markDanger, 'w-full justify-start normal-case tracking-normal text-[11px]')}>
            <TriangleAlert size={12} className="flex-shrink-0" />
            {error}
          </div>
        )}

        <Field label="Title" required hint="Shown in the app and used as the SMS text">
          <Input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Half-term closure"
            autoFocus
          />
        </Field>

        <Field label="Notice" required>
          <textarea
            className={cn(styles.input, 'min-h-[110px] resize-y')}
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            placeholder="School closes on Friday 24th and reopens Monday 3rd. Normal pickup times apply."
          />
        </Field>

        <label className="flex items-center gap-2 text-[11.5px] font-medium text-foreground">
          <input
            type="checkbox"
            className={styles.checkbox}
            checked={form.priority === 'urgent'}
            onChange={(e) => setForm({ ...form, priority: e.target.checked ? 'urgent' : 'normal' })}
          />
          Mark urgent — pinned to the top of every parent's list
        </label>

        <div>
          <p className={cn(styles.label, 'mb-1.5')}>Who it goes to</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1.5">
            {groups.map((g) => (
              <label key={g._id} className="flex items-center gap-1.5 text-[11.5px] text-foreground">
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={form.groupIds.includes(g._id)}
                  onChange={() => toggleGroup(g._id)}
                />
                {g.name}
              </label>
            ))}
          </div>
          <p className={cn(styles.meta, 'mt-1.5')}>
            {form.groupIds.length === 0
              ? 'Nothing ticked — this goes to every parent in the school.'
              : `${form.groupIds.length} group${form.groupIds.length === 1 ? '' : 's'} selected.`}
          </p>
        </div>

        <div className={cn(styles.panel, 'flex flex-col gap-2 p-3')}>
          <p className={styles.sectionTitle}>How it reaches them</p>
          <p className={cn(styles.meta, '-mt-1')}>
            It always appears in the parent app. These add a notification that it has.
          </p>
          <label className="flex items-center gap-2 text-[11.5px] text-foreground">
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={form.sendPush}
              onChange={(e) => setForm({ ...form, sendPush: e.target.checked })}
            />
            Push notification — free
          </label>
          <label className="flex items-center gap-2 text-[11.5px] text-foreground">
            <input
              type="checkbox"
              className={styles.checkbox}
              checked={form.sendSms}
              onChange={(e) => setForm({ ...form, sendSms: e.target.checked })}
            />
            SMS — <span className="font-semibold">one message per parent, billed per message</span>
          </label>
          {form.sendSms && (
            <p className={cn(styles.markWarning, 'w-full justify-start normal-case tracking-normal text-[11px]')}>
              Check the preview before publishing — it shows exactly how many messages this sends.
            </p>
          )}
        </div>

        <Field label="Schedule for" hint="Leave blank to publish by hand">
          <Input
            type="datetime-local"
            value={form.scheduledFor}
            onChange={(e) => setForm({ ...form, scheduledFor: e.target.value })}
          />
        </Field>
      </form>
    </Modal>
  );
}
